import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Paper,
    Typography,
    Grid,
    Card,
    CardContent,
    Chip,
    Alert,
    Skeleton,
    Button,
    Stack,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TablePagination,
    TextField,
    Autocomplete,
    IconButton,
    Collapse,
    Tooltip,
    Link
} from '@mui/material';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend,
    ResponsiveContainer,
    LineChart,
    Line,
    PieChart,
    Pie,
    Cell
} from 'recharts';
import {
    ArrowBack as ArrowBackIcon,
    FilterList as FilterListIcon,
    Clear as ClearIcon,
    Person as PersonIcon,
    AttachMoney as MoneyIcon,
    EventNote as AppointmentIcon,
    MedicalServices as ScanIcon,
    TrendingUp as ProfitIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    PictureAsPdf as PdfIcon,
    Download as DownloadIcon,
    Visibility as ViewIcon,
    Description as ReportIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { userAPI, API_URL } from '../../services/api';
import { useRTL } from '../../hooks/useRTL';
import { COLORS, STATUS_COLORS } from "../../constants/colors"
// Color palette for charts


// Loading skeleton component
const StatsSkeleton = () => (
    <Box sx={{ p: 3 }}>
        <Skeleton variant="text" sx={{ fontSize: '2rem', width: '40%', mb: 2 }} />
        <Grid container spacing={2} sx={{ mb: 4 }}>
            {[...Array(5)].map((_, index) => (
                <Grid item xs={12} sm={6} md={2.4} key={index}>
                    <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
                </Grid>
            ))}
        </Grid>
        <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 1, mb: 3 }} />
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 1 }} />
    </Box>
);

// Chart wrapper component
const ChartWrapper = ({ title, children, height = 400 }) => (
    <Card sx={{ mb: 3, overflow: 'visible' }}>
        <CardContent>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                {title}
            </Typography>
            <Box sx={{ width: '100%', height: height, position: 'relative' }}>
                {children}
            </Box>
        </CardContent>
    </Card>
);

// Summary card component
const SummaryCard = ({ title, value, icon: Icon, color, subtitle }) => (
    <Card sx={{ height: '100%', bgcolor: `${color}.main`, color: 'white' }}>
        <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        {title}
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>
                        {typeof value === 'number' ? value.toLocaleString() : value}
                    </Typography>
                    {subtitle && (
                        <Typography variant="caption" sx={{ opacity: 0.8 }}>
                            {subtitle}
                        </Typography>
                    )}
                </Box>
                <Icon sx={{ fontSize: 48, opacity: 0.3 }} />
            </Box>
        </CardContent>
    </Card>
);

// Report Actions Component
const ReportActions = ({ appointment, t }) => {
    const hasReport = appointment.status === 'completed' && appointment.pdfReport;

    // Construct the full URL for the PDF
    const getReportUrl = () => {
        if (!appointment.pdfReport) return null;
        // If the pdfReport already has full URL, use it; otherwise prepend API_URL
        if (appointment.pdfReport.startsWith('http')) {
            return appointment.pdfReport;
        }
        // Remove /api from API_URL if present and append the report path
        const baseUrl = API_URL.replace('/api', '');
        return `${baseUrl}${appointment.pdfReport}`;
    };

    const handleView = () => {
        const url = getReportUrl();
        if (url) {
            window.open(url, '_blank');
        }
    };

    const handleDownload = () => {
        const url = getReportUrl();
        if (url) {
            const link = document.createElement('a');
            link.href = url;
            link.download = `report_${appointment._id}.pdf`;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    if (!hasReport) {
        return (
            <Tooltip title={t('radiologistStats.noReportAvailable', 'No report available')}>
                <span>
                    <IconButton size="small" disabled>
                        <ReportIcon color="disabled" />
                    </IconButton>
                </span>
            </Tooltip>
        );
    }

    return (
        <Stack direction="row" spacing={0.5}>
            <Tooltip title={t('radiologistStats.viewReport', 'View Report')}>
                <IconButton size="small" color="primary" onClick={handleView}>
                    <ViewIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Tooltip title={t('radiologistStats.downloadReport', 'Download Report')}>
                <IconButton size="small" color="secondary" onClick={handleDownload}>
                    <DownloadIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </Stack>
    );
};

// Scans Display Component
const ScansDisplay = ({ scans, t }) => {
    if (!scans || scans.length === 0) {
        return <Typography variant="body2" color="text.secondary">-</Typography>;
    }

    return (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {scans.map((scanItem, index) => (
                <Chip
                    key={index}
                    label={`${scanItem.scan?.name || 'Unknown'}${scanItem.quantity > 1 ? ` (×${scanItem.quantity})` : ''}`}
                    size="small"
                    variant="outlined"
                    color="primary"
                    sx={{ mb: 0.5 }}
                />
            ))}
        </Stack>
    );
};

const RadiologistStats = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const { isRTL } = useRTL();

    // State
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [statistics, setStatistics] = useState(null);
    const [filterOptions, setFilterOptions] = useState(null);
    const [filtersExpanded, setFiltersExpanded] = useState(false);

    // Pagination state
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0
    });

    // Filter state
    const [filters, setFilters] = useState({
        startDate: null,
        endDate: null,
        branchId: '',
        scanCategoryId: '',
        scanId: '',
        patientId: '',
        status: ''
    });

    // Check if any filters are active
    const hasActiveFilters = Object.entries(filters).some(([key, value]) => {
        if (key === 'startDate' || key === 'endDate') return value !== null;
        return value !== '';
    });

    // Fetch filter options
    const fetchFilterOptions = useCallback(async () => {
        try {
            const response = await userAPI.getRadiologistStatsFilterOptions(id);
            setFilterOptions(response.data?.data || response.data);
        } catch (err) {
            console.error('Error fetching filter options:', err);
        }
    }, [id]);

    // Fetch statistics
    const fetchStatistics = useCallback(async (currentPage = pagination.page, currentLimit = pagination.limit) => {
        try {
            setLoading(true);
            setError(null);

            const params = {
                page: currentPage,
                limit: currentLimit
            };

            if (filters.startDate) params.startDate = moment(filters.startDate).format('YYYY-MM-DD');
            if (filters.endDate) params.endDate = moment(filters.endDate).format('YYYY-MM-DD');
            if (filters.branchId) params.branchId = filters.branchId;
            if (filters.scanCategoryId) params.scanCategoryId = filters.scanCategoryId;
            if (filters.scanId) params.scanId = filters.scanId;
            if (filters.patientId) params.patientId = filters.patientId;
            if (filters.status) params.status = filters.status;

            const response = await userAPI.getRadiologistStatistics(id, params);
            const data = response.data?.data || response.data;

            setStatistics(data);

            // Update pagination from response
            if (data.appointments?.pagination) {
                setPagination(prev => ({
                    ...prev,
                    ...data.appointments.pagination
                }));
            }
        } catch (err) {
            console.error('Error fetching statistics:', err);
            setError(err.response?.data?.message || t('radiologistStats.errorFetching', 'Error fetching statistics'));
        } finally {
            setLoading(false);
        }
    }, [id, filters, t]);

    // Initial fetch
    useEffect(() => {
        fetchFilterOptions();
    }, [fetchFilterOptions]);

    // Fetch on filter/pagination change
    useEffect(() => {
        fetchStatistics(pagination.page, pagination.limit);
    }, [pagination.page, pagination.limit]); // eslint-disable-line react-hooks/exhaustive-deps

    // Handle filter changes
    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    // Clear all filters
    const clearFilters = () => {
        setFilters({
            startDate: null,
            endDate: null,
            branchId: '',
            scanCategoryId: '',
            scanId: '',
            patientId: '',
            status: ''
        });
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    // Apply filters (re-fetch from page 1)
    const applyFilters = () => {
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchStatistics(1, pagination.limit);
    };

    // Handle page change
    const handlePageChange = (event, newPage) => {
        setPagination(prev => ({ ...prev, page: newPage + 1 })); // MUI TablePagination is 0-indexed
    };

    // Handle rows per page change
    const handleRowsPerPageChange = (event) => {
        const newLimit = parseInt(event.target.value, 10);
        setPagination(prev => ({ ...prev, limit: newLimit, page: 1 }));
    };

    // Status options
    const statusOptions = [
        { value: '', label: t('common.all', 'All') },
        { value: 'scheduled', label: t('appointments.statusScheduled', 'Scheduled') },
        { value: 'in_progress', label: t('status.in_progress', 'In Progress') },
        { value: 'completed', label: t('appointments.statusCompleted', 'Completed') },
        { value: 'cancelled', label: t('appointments.statusCancelled', 'Cancelled') },
        { value: 'no_show', label: t('appointments.statusNoShow', 'No Show') }
    ];

    if (loading && !statistics) {
        return <StatsSkeleton />;
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/radiologists')} sx={{ mb: 2 }}>
                    {t('common.back', 'Back')}
                </Button>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    const { radiologist, totals, charts, appointments } = statistics || {};

    return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
            <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>
                {/* Header */}
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                    <Box display="flex" alignItems="center" gap={2}>
                        <IconButton onClick={() => navigate('/radiologists')}>
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h4" component="h1">
                            {t('radiologistStats.title', 'Radiologist Statistics')}
                        </Typography>
                    </Box>
                    <Button
                        variant="outlined"
                        startIcon={filtersExpanded ? <ExpandLessIcon /> : <FilterListIcon />}
                        onClick={() => setFiltersExpanded(!filtersExpanded)}
                    >
                        {t('common.filters', 'Filters')}
                        {hasActiveFilters && (
                            <Chip
                                size="small"
                                label={Object.values(filters).filter(v => v !== '' && v !== null).length}
                                color="primary"
                                sx={{ ml: 1 }}
                            />
                        )}
                    </Button>
                </Box>

                {/* Radiologist Info Card */}
                {radiologist && (
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Box display="flex" alignItems="center" gap={3}>
                                <PersonIcon sx={{ fontSize: 64, color: 'primary.main' }} />
                                <Box flex={1}>
                                    <Typography variant="h5" gutterBottom>
                                        {radiologist.name}
                                    </Typography>
                                    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                                        <Chip
                                            label={radiologist.isActive ? t('common.active', 'Active') : t('common.inactive', 'Inactive')}
                                            color={radiologist.isActive ? 'success' : 'default'}
                                            size="small"
                                        />
                                        {radiologist.gender && (
                                            <Chip label={t(`genders.${radiologist.gender}`, radiologist.gender)} size="small" variant="outlined" />
                                        )}
                                        {radiologist.age && (
                                            <Chip label={`${radiologist.age} ${t('patients.years', 'years')}`} size="small" variant="outlined" />
                                        )}
                                        {radiologist.branch?.name && (
                                            <Chip label={radiologist.branch.name} size="small" color="primary" variant="outlined" />
                                        )}
                                    </Stack>
                                    {radiologist.phoneNumber && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                            {t('radiologists.phoneNumber', 'Phone')}: {radiologist.phoneNumber}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                )}

                {/* Filters Panel */}
                <Collapse in={filtersExpanded}>
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            <FilterListIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                            {t('common.filters', 'Filters')}
                        </Typography>
                        <Grid container spacing={2} alignItems="center">
                            {/* Date Range */}
                            <Grid item xs={12} sm={6} md={3}>
                                <DatePicker
                                    label={t('common.startDate', 'Start Date')}
                                    value={filters.startDate}
                                    onChange={(date) => handleFilterChange('startDate', date)}
                                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <DatePicker
                                    label={t('common.endDate', 'End Date')}
                                    value={filters.endDate}
                                    onChange={(date) => handleFilterChange('endDate', date)}
                                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                />
                            </Grid>

                            {/* Branch */}
                            <Grid item xs={12} sm={6} md={3}>
                                <Autocomplete
                                    options={filterOptions?.branches || []}
                                    getOptionLabel={(option) => option.name || ''}
                                    value={filterOptions?.branches?.find(b => b._id === filters.branchId) || null}
                                    onChange={(_, newValue) => handleFilterChange('branchId', newValue?._id || '')}
                                    renderInput={(params) => (
                                        <TextField {...params} label={t('appointments.branch', 'Branch')} size="small" />
                                    )}
                                />
                            </Grid>

                            {/* Status */}
                            <Grid item xs={12} sm={6} md={3}>
                                <Autocomplete
                                    options={statusOptions}
                                    getOptionLabel={(option) => option.label}
                                    value={statusOptions.find(s => s.value === filters.status) || statusOptions[0]}
                                    onChange={(_, newValue) => handleFilterChange('status', newValue?.value || '')}
                                    renderInput={(params) => (
                                        <TextField {...params} label={t('common.status', 'Status')} size="small" />
                                    )}
                                    disableClearable
                                />
                            </Grid>

                            {/* Scan Category */}
                            <Grid item xs={12} sm={6} md={3}>
                                <Autocomplete
                                    options={filterOptions?.scanCategories || []}
                                    getOptionLabel={(option) => option.name || ''}
                                    value={filterOptions?.scanCategories?.find(c => c._id === filters.scanCategoryId) || null}
                                    onChange={(_, newValue) => handleFilterChange('scanCategoryId', newValue?._id || '')}
                                    renderInput={(params) => (
                                        <TextField {...params} label={t('scans.category', 'Scan Category')} size="small" />
                                    )}
                                />
                            </Grid>

                            {/* Scan Type */}
                            <Grid item xs={12} sm={6} md={3}>
                                <Autocomplete
                                    options={filterOptions?.scans || []}
                                    getOptionLabel={(option) => option.name || ''}
                                    value={filterOptions?.scans?.find(s => s._id === filters.scanId) || null}
                                    onChange={(_, newValue) => handleFilterChange('scanId', newValue?._id || '')}
                                    renderInput={(params) => (
                                        <TextField {...params} label={t('scans.scanType', 'Scan Type')} size="small" />
                                    )}
                                />
                            </Grid>

                            {/* Patient */}
                            <Grid item xs={12} sm={6} md={3}>
                                <Autocomplete
                                    options={filterOptions?.patients || []}
                                    getOptionLabel={(option) => `${option.name}${option.phoneNumber ? ` (${option.phoneNumber})` : ''}`}
                                    value={filterOptions?.patients?.find(p => p._id === filters.patientId) || null}
                                    onChange={(_, newValue) => handleFilterChange('patientId', newValue?._id || '')}
                                    renderInput={(params) => (
                                        <TextField {...params} label={t('appointments.patient', 'Patient')} size="small" />
                                    )}
                                />
                            </Grid>

                            {/* Action Buttons */}
                            <Grid item xs={12} sm={6} md={3}>
                                <Stack direction="row" spacing={1}>
                                    <Button variant="contained" onClick={applyFilters} fullWidth>
                                        {t('common.apply', 'Apply')}
                                    </Button>
                                    {hasActiveFilters && (
                                        <Button variant="outlined" onClick={clearFilters} startIcon={<ClearIcon />}>
                                            {t('common.clear', 'Clear')}
                                        </Button>
                                    )}
                                </Stack>
                            </Grid>
                        </Grid>
                    </Paper>
                </Collapse>

                {/* Summary Cards */}
                {totals && (
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6} md={2.4}>
                            <SummaryCard
                                title={t('radiologistStats.totalAppointments', 'Total Appointments')}
                                value={totals.totalAppointments}
                                icon={AppointmentIcon}
                                color="primary"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2.4}>
                            <SummaryCard
                                title={t('radiologistStats.totalIncome', 'Total Income')}
                                value={`${totals.totalIncome?.toLocaleString()} EGP`}
                                icon={MoneyIcon}
                                color="success"
                                subtitle={`${t('common.avg', 'Avg')}: ${totals.avgIncomePerAppointment?.toFixed(0)} EGP`}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2.4}>
                            <SummaryCard
                                title={t('radiologistStats.totalProfit', 'Total Profit')}
                                value={`${totals.totalProfit?.toLocaleString()} EGP`}
                                icon={ProfitIcon}
                                color="info"
                                subtitle={`${t('common.avg', 'Avg')}: ${totals.avgProfitPerAppointment?.toFixed(0)} EGP`}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2.4}>
                            <SummaryCard
                                title={t('radiologistStats.totalScans', 'Total Scans')}
                                value={totals.totalScans}
                                icon={ScanIcon}
                                color="warning"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2.4}>
                            <SummaryCard
                                title={t('radiologistStats.totalPatients', 'Unique Patients')}
                                value={totals.totalPatients}
                                icon={PersonIcon}
                                color="secondary"
                            />
                        </Grid>
                    </Grid>
                )}

                {/* Status Summary Row */}
                {totals && (
                    <Paper sx={{ p: 2, mb: 3 }}>
                        <Stack direction="row" spacing={3} justifyContent="center" flexWrap="wrap" useFlexGap>
                            <Chip
                                label={`${t('appointments.statusCompleted', 'Completed')}: ${totals.completedCount}`}
                                sx={{ bgcolor: STATUS_COLORS.completed, color: 'white' }}
                            />
                            <Chip
                                label={`${t('appointments.statusScheduled', 'Scheduled')}: ${totals.scheduledCount}`}
                                sx={{ bgcolor: STATUS_COLORS.scheduled, color: 'white' }}
                            />
                            <Chip
                                label={`${t('appointments.statusCancelled', 'Cancelled')}: ${totals.cancelledCount}`}
                                sx={{ bgcolor: STATUS_COLORS.cancelled, color: 'white' }}
                            />
                        </Stack>
                    </Paper>
                )}

                {/* Charts */}
                {charts && (
                    <>
                        {/* Monthly Appointments Bar Chart */}
                        <ChartWrapper title={t('radiologistStats.monthlyAppointments', 'Monthly Appointments')}>
                            {charts.monthlyStats?.length > 0 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={charts.monthlyStats}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="month" />
                                        <YAxis />
                                        <RechartsTooltip />
                                        <Legend />
                                        <Bar
                                            dataKey="appointments"
                                            name={t('radiologistStats.appointments', 'Appointments')}
                                            fill="#8884d8"
                                            radius={[4, 4, 0, 0]}
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <Alert severity="info">{t('radiologistStats.noDataAvailable', 'No data available')}</Alert>
                            )}
                        </ChartWrapper>

                        {/* Income & Profit Line Chart */}
                        <ChartWrapper title={t('radiologistStats.monthlyIncomeProfit', 'Monthly Income & Profit')}>
                            {charts.monthlyStats?.length > 0 ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={charts.monthlyStats}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="month" />
                                        <YAxis />
                                        <RechartsTooltip formatter={(value) => `${value.toLocaleString()} EGP`} />
                                        <Legend />
                                        <Line
                                            type="monotone"
                                            dataKey="income"
                                            name={t('radiologistStats.income', 'Income')}
                                            stroke="#82ca9d"
                                            strokeWidth={3}
                                            dot={{ r: 5 }}
                                            activeDot={{ r: 8 }}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="profit"
                                            name={t('radiologistStats.profit', 'Profit')}
                                            stroke="#8884d8"
                                            strokeWidth={3}
                                            dot={{ r: 5 }}
                                            activeDot={{ r: 8 }}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            ) : (
                                <Alert severity="info">{t('radiologistStats.noDataAvailable', 'No data available')}</Alert>
                            )}
                        </ChartWrapper>

                        {/* Status & Branch Charts Side by Side */}
                        <Grid container spacing={3} sx={{ mb: 3 }}>
                            {/* Status Pie Chart */}
                            <Grid item xs={12} md={6}>
                                <Card sx={{ height: '100%' }}>
                                    <CardContent>
                                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                                            {t('radiologistStats.appointmentsByStatus', 'Appointments by Status')}
                                        </Typography>
                                        <Box sx={{ width: '100%', height: 350 }}>
                                            {charts.statusDistribution?.length > 0 ? (
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <PieChart>
                                                        <Pie
                                                            data={charts.statusDistribution}
                                                            cx="50%"
                                                            cy="50%"
                                                            labelLine={true}
                                                            label={({ name, percent }) => `${t(`appointments.status${name.charAt(0).toUpperCase() + name.slice(1).replace('_', '')}`, name)} (${(percent * 100).toFixed(0)}%)`}
                                                            outerRadius={100}
                                                            dataKey="value"
                                                        >
                                                            {charts.statusDistribution.map((entry, index) => (
                                                                <Cell
                                                                    key={`cell-${index}`}
                                                                    fill={STATUS_COLORS[entry.name] || COLORS[index % COLORS.length]}
                                                                />
                                                            ))}
                                                        </Pie>
                                                        <RechartsTooltip />
                                                    </PieChart>
                                                </ResponsiveContainer>
                                            ) : (
                                                <Alert severity="info">{t('radiologistStats.noDataAvailable', 'No data available')}</Alert>
                                            )}
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Branch Bar Chart */}
                            <Grid item xs={12} md={6}>
                                <Card sx={{ height: '100%' }}>
                                    <CardContent>
                                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                                            {t('radiologistStats.appointmentsByBranch', 'Appointments by Branch')}
                                        </Typography>
                                        <Box sx={{ width: '100%', height: 350 }}>
                                            {charts.branchDistribution?.length > 0 ? (
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <BarChart data={charts.branchDistribution} layout="vertical">
                                                        <CartesianGrid strokeDasharray="3 3" />
                                                        <XAxis type="number" />
                                                        <YAxis dataKey="name" type="category" width={100} />
                                                        <RechartsTooltip />
                                                        <Bar dataKey="appointments" fill="#00C49F" radius={[0, 4, 4, 0]} />
                                                    </BarChart>
                                                </ResponsiveContainer>
                                            ) : (
                                                <Alert severity="info">{t('radiologistStats.noDataAvailable', 'No data available')}</Alert>
                                            )}
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>

                        {/* Scan Type & Category Charts */}
                        <Grid container spacing={3} sx={{ mb: 3 }}>
                            {/* Top Scans */}
                            <Grid item xs={12} md={6}>
                                <ChartWrapper title={t('radiologistStats.topScans', 'Top Scans Performed')} height={350}>
                                    {charts.scanTypeDistribution?.length > 0 ? (
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={charts.scanTypeDistribution} layout="vertical">
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis type="number" />
                                                <YAxis dataKey="name" type="category" width={120} />
                                                <RechartsTooltip />
                                                <Bar dataKey="count" fill="#FF8042" radius={[0, 4, 4, 0]} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    ) : (
                                        <Alert severity="info">{t('radiologistStats.noDataAvailable', 'No data available')}</Alert>
                                    )}
                                </ChartWrapper>
                            </Grid>

                            {/* Scan Categories Pie Chart */}
                            <Grid item xs={12} md={6}>
                                <Card sx={{ height: '100%' }}>
                                    <CardContent>
                                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                                            {t('radiologistStats.scansByCategory', 'Scans by Category')}
                                        </Typography>
                                        <Box sx={{ width: '100%', height: 350 }}>
                                            {charts.scanCategoryDistribution?.length > 0 ? (
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <PieChart>
                                                        <Pie
                                                            data={charts.scanCategoryDistribution}
                                                            cx="50%"
                                                            cy="50%"
                                                            labelLine={true}
                                                            label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                                                            outerRadius={100}
                                                            dataKey="count"
                                                        >
                                                            {charts.scanCategoryDistribution.map((entry, index) => (
                                                                <Cell
                                                                    key={`cell-${index}`}
                                                                    fill={entry.color || COLORS[index % COLORS.length]}
                                                                />
                                                            ))}
                                                        </Pie>
                                                        <RechartsTooltip />
                                                    </PieChart>
                                                </ResponsiveContainer>
                                            ) : (
                                                <Alert severity="info">{t('radiologistStats.noDataAvailable', 'No data available')}</Alert>
                                            )}
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </>
                )}

                {/* Monthly Stats Table */}
                {charts?.monthlyStats?.length > 0 && (
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                                {t('radiologistStats.monthlyStatsTable', 'Monthly Statistics Details')}
                            </Typography>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: 'primary.main' }}>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                                            {t('radiologistStats.month', 'Month')}
                                        </TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">
                                            {t('radiologistStats.appointments', 'Appointments')}
                                        </TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">
                                            {t('radiologistStats.scans', 'Scans')}
                                        </TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">
                                            {t('radiologistStats.income', 'Income')}
                                        </TableCell>
                                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">
                                            {t('radiologistStats.profit', 'Profit')}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {charts.monthlyStats
                                        .slice()
                                        .reverse()
                                        .map((row) => (
                                            <TableRow key={row.month} hover>
                                                <TableCell>{row.month}</TableCell>
                                                <TableCell align="center">{row.appointments}</TableCell>
                                                <TableCell align="center">{row.scans}</TableCell>
                                                <TableCell align="right">{row.income.toLocaleString()} EGP</TableCell>
                                                <TableCell align="right">{row.profit.toLocaleString()} EGP</TableCell>
                                            </TableRow>
                                        ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                )}

                {/* All Appointments Table with Pagination */}
                <Card>
                    <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                {t('radiologistStats.allAppointments', 'All Appointments')}
                            </Typography>
                            <Chip
                                label={`${pagination.total} ${t('common.total', 'total')}`}
                                color="primary"
                                variant="outlined"
                                size="small"
                            />
                        </Box>

                        {appointments?.data?.length > 0 ? (
                            <>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold' }}>
                                                {t('appointments.patient', 'Patient')}
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>
                                                {t('appointments.date', 'Date')}
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>
                                                {t('radiologistStats.scansRequested', 'Scans')}
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>
                                                {t('appointments.branch', 'Branch')}
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>
                                                {t('common.status', 'Status')}
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }} align="right">
                                                {t('appointments.price', 'Price')}
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }} align="center">
                                                {t('radiologistStats.report', 'Report')}
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {appointments.data.map((apt) => (
                                            <TableRow key={apt._id} hover>
                                                <TableCell>
                                                    <Box>
                                                        <Typography variant="body2" fontWeight="medium">
                                                            {apt.patientId?.name || 'N/A'}
                                                        </Typography>
                                                        {apt.patientId?.phoneNumber && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                {apt.patientId.phoneNumber}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Box>
                                                        <Typography variant="body2">
                                                            {moment(apt.scheduledAt).format('YYYY-MM-DD')}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {moment(apt.scheduledAt).format('HH:mm')}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <ScansDisplay scans={apt.scans} t={t} />
                                                </TableCell>
                                                <TableCell>{apt.branch?.name || 'N/A'}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={t(`appointments.status${apt.status?.charAt(0).toUpperCase() + apt.status?.slice(1).replace('_', '')}`, apt.status)}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: STATUS_COLORS[apt.status],
                                                            color: 'white'
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {(apt.price || 0).toLocaleString()} EGP
                                                    </Typography>
                                                    {apt.discountAmount > 0 && (
                                                        <Typography variant="caption" color="error">
                                                            -{apt.discountAmount} ({apt.salePercentage}%)
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell align="center">
                                                    <ReportActions appointment={apt} t={t} />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>

                                {/* Pagination */}
                                <TablePagination
                                    component="div"
                                    count={pagination.total}
                                    page={pagination.page - 1} // MUI is 0-indexed
                                    onPageChange={handlePageChange}
                                    rowsPerPage={pagination.limit}
                                    onRowsPerPageChange={handleRowsPerPageChange}
                                    rowsPerPageOptions={[5, 10, 25, 50]}
                                    labelRowsPerPage={t('common.rowsPerPage', 'Rows per page')}
                                    labelDisplayedRows={({ from, to, count }) =>
                                        `${from}-${to} ${t('common.of', 'of')} ${count}`
                                    }
                                />
                            </>
                        ) : (
                            <Alert severity="info">
                                {t('radiologistStats.noAppointmentsFound', 'No appointments found')}
                            </Alert>
                        )}
                    </CardContent>
                </Card>
            </Box>
        </LocalizationProvider>
    );
};

export default RadiologistStats;